import React from "react";
import { Check, Minus, Crown, Wrench, UserRound } from "lucide-react";
import { PageHeader } from "../components/core/Layout";
import { cn } from "../lib/utils";

export const ROLES = [
  { key: "admin", label: "Admin", icon: Crown, blurb: "Full control of the organisation: users, settings, approvals and Geotab credentials." },
  { key: "operator", label: "Manager", icon: Wrench, blurb: "Runs the fleet day to day: vehicles, compliance, alerts, telematics devices, car-pool hand-overs." },
  { key: "staff", label: "Staff", icon: UserRound, blurb: "Drivers and employees: view the fleet, book pool vehicles and submit requests." },
] as const;

const MATRIX: { area: string; rows: { label: string; admin: boolean; operator: boolean; staff: boolean }[] }[] = [
  { area: "Fleet", rows: [
    { label: "View vehicles, documents & photos", admin: true, operator: true, staff: true },
    { label: "Edit vehicle details / archive", admin: true, operator: true, staff: false },
    { label: "Add or edit compliance records (road tax, insurance, PUSPAKOM…)", admin: true, operator: true, staff: false },
    { label: "Acknowledge / resolve alerts, run scans", admin: true, operator: true, staff: false },
  ] },
  { area: "Telematics", rows: [
    { label: "View live map, trips & sync logs", admin: true, operator: true, staff: true },
    { label: "Link Geotab devices to vehicles", admin: true, operator: true, staff: false },
    { label: "Manage Geotab credentials", admin: true, operator: false, staff: false },
  ] },
  { area: "Car Pool", rows: [
    { label: "Book pool vehicles, view own bookings", admin: true, operator: true, staff: true },
    { label: "Bookings confirmed instantly (no approval)", admin: true, operator: true, staff: false },
    { label: "Approve bookings, hand-over & return, manage pool vehicles", admin: true, operator: true, staff: false },
    { label: "Booking approval preset & limits", admin: true, operator: false, staff: false },
  ] },
  { area: "Requests & Approvals", rows: [
    { label: "Submit maintenance / replacement requests with quotes", admin: true, operator: true, staff: true },
    { label: "Act as an approver", admin: true, operator: true, staff: false },
    { label: "Configure approval flows", admin: true, operator: false, staff: false },
  ] },
  { area: "Organisation", rows: [
    { label: "Manage users & access levels", admin: true, operator: false, staff: false },
    { label: "Organisation profile & contacts", admin: true, operator: false, staff: false },
    { label: "Audit trail", admin: true, operator: true, staff: false },
  ] },
];

const Tick = ({ on }: { on: boolean }) => <span className={cn("mx-auto flex h-6 w-6 items-center justify-center rounded-full", on ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-300")}>{on ? <Check className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}</span>;

export function AccessLevelsContent() {
  return (
    <div data-testid="access-levels-content">
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        {ROLES.map((r) => <div key={r.key} data-testid={`role-card-${r.key}`} className="rounded-lg border border-slate-200 p-3"><div className="flex items-start gap-3"><span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[color:var(--brand)] text-[color:var(--brand-fg)]"><r.icon className="h-4 w-4" /></span><div><p className="text-sm font-semibold text-slate-900">{r.label}</p><p className="mt-0.5 text-xs text-slate-600">{r.blurb}</p></div></div></div>)}
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full min-w-[560px] text-sm" data-testid="access-matrix">
          <thead className="border-b border-slate-200 bg-slate-50"><tr><th className="th">Capability</th>{ROLES.map((r) => <th key={r.key} className="th text-center">{r.label}</th>)}</tr></thead>
          <tbody>
            {MATRIX.map((g) => (
              <React.Fragment key={g.area}>
                <tr className="bg-slate-50/60"><td colSpan={4} className="px-4 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">{g.area}</td></tr>
                {g.rows.map((row) => <tr key={row.label} className="row-hover"><td className="td py-1.5 text-xs">{row.label}</td><td className="td py-1.5"><Tick on={row.admin} /></td><td className="td py-1.5"><Tick on={row.operator} /></td><td className="td py-1.5"><Tick on={row.staff} /></td></tr>)}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AccessLevelsPage() {
  return (
    <div data-testid="access-levels-page">
      <PageHeader eyebrow="Settings" title="Access levels" subtitle="Three preset levels for your organization's users. Assign them under Settings → Users." />
      <AccessLevelsContent />
    </div>
  );
}
